import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Stethoscope, Shield, Users, BookOpen, UserPlus, LogIn, ChevronDown, Heart, User, LogOut } from '../lib/icons';
import { supabase } from '../lib/supabase';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDoctorMenuOpen, setIsDoctorMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check if admin is logged in
  const isAdminLoggedIn = localStorage.getItem('adminLoggedIn') === 'true';
  
  // Check if provider is logged in
  const isProviderLoggedIn = localStorage.getItem('providerLoggedIn') === 'true';

  const navigation = [
    { name: 'Anbieter', path: '/anbieter', icon: Stethoscope },
    { name: 'Symptome', path: '/symptome', icon: BookOpen },
    { name: 'Krankheiten', path: '/krankheiten', icon: BookOpen },
    { name: 'Beratung', path: '/beratung', icon: BookOpen },
    { name: 'Wissen', path: '/wissen', icon: BookOpen },
  ];

  const doctorMenuItems = [
    { name: 'Registrierung', path: '/register', icon: UserPlus },
    { name: 'Login', path: '/provider-login', icon: LogIn },
  ];

  const handleProviderLogout = async () => {
    try {
      // Sign out from Supabase Auth
      await supabase.auth.signOut();
      
      // Clear local storage
      localStorage.removeItem('providerLoggedIn');
      localStorage.removeItem('providerId');
      
      // Navigate to home page
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      // Even if Supabase logout fails, clear local storage and redirect
      localStorage.removeItem('providerLoggedIn');
      localStorage.removeItem('providerId');
      navigate('/');
    }
  };

  const isActivePath = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center"
            onClick={() => {
              // Scroll to top when logo is clicked on homepage
              if (location.pathname === '/') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
            aria-label="Zur Startseite"
          >
            <div className="bg-blue-600 p-2 rounded-lg mr-3">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <div className="text-2xl font-bold text-gray-900">Behandlung.de</div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex flex-1 justify-between items-center ml-8">
            {/* Main Navigation - Left Side */}
            <nav className="flex space-x-8">
              {navigation.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium transition-colors ${
                    isActivePath(item.path)
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                  aria-current={isActivePath(item.path) ? 'page' : undefined}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.name}</span>
                </Link>
              ))}
            </nav>
            
            {/* Right Side Navigation */}
            <div className="flex items-center space-x-4">
              {/* Admin Link - only show if admin is logged in */}
              {isAdminLoggedIn && !isProviderLoggedIn && (
                <Link
                  to="/admin"
                  className="flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                  aria-label="Zum Admin-Dashboard"
                >
                  <Shield className="w-5 h-5" />
                  <span>Admin</span>
                </Link>
              )}
              
              {/* Provider Dashboard Link - only show if provider is logged in */}
              {isProviderLoggedIn && (
                <Link
                  to="/provider-dashboard"
                  className="flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                  aria-label="Zum Anbieter-Dashboard"
                >
                  <User className="w-6 h-6" />
                  <span>Profil</span>
                </Link>
              )}
              
              {/* Provider Logout Button - only show if provider is logged in */}
              {isProviderLoggedIn && (
                <button
                  onClick={handleProviderLogout}
                  className="flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                  aria-label="Vom Anbieter-Konto abmelden"
                >
                  <LogOut className="w-6 h-6" />
                  <span>Abmelden</span>
                </button>
              )}
              
              {/* Für Ärzte Dropdown - only show if provider is NOT logged in */}
              {!isProviderLoggedIn && (
                <div className="relative">
                  <button
                    onClick={() => setIsDoctorMenuOpen(!isDoctorMenuOpen)}
                    className="flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                    aria-label="Anbieter-Menü öffnen"
                    aria-expanded={isDoctorMenuOpen}
                    aria-haspopup="true"
                  >
                    <Users className="w-5 h-5" />
                    <span>Anbieter</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  
                  {isDoctorMenuOpen && (
                    <div className="absolute top-full right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                      {doctorMenuItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setIsDoctorMenuOpen(false)}
                          className="flex items-center space-x-2 w-full px-4 py-3 text-left text-base text-gray-700 hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg transition-colors"
                        >
                          <item.icon className="w-5 h-5" />
                          <span>{item.name}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                  
                  {/* Click outside to close dropdown */}
                  {isDoctorMenuOpen && (
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setIsDoctorMenuOpen(false)}
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Menü schließen" : "Menü öffnen"}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          <div className="px-4 py-2 space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMenuOpen(false)}
                className={`flex items-center space-x-2 w-full px-3 py-2 rounded-md text-left text-base font-medium transition-colors ${
                  isActivePath(item.path)
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.name}</span>
              </Link>
            ))}
            
            {/* Mobile Admin Link */}
            {isAdminLoggedIn && !isProviderLoggedIn && (
              <Link
                to="/admin"
                onClick={() => setIsMenuOpen(false)}
                className="flex items-center space-x-2 w-full px-3 py-2 rounded-md text-left text-base font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                aria-label="Zum Admin-Dashboard"
              >
                <Shield className="w-5 h-5" />
                <span>Admin</span>
              </Link>
            )}
            
            {/* Mobile Provider Dashboard Link */}
            {isProviderLoggedIn && (
              <Link
                to="/provider-dashboard"
                onClick={() => setIsMenuOpen(false)}
                className="flex items-center space-x-2 w-full px-3 py-2 rounded-md text-left text-base font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                aria-label="Zum Anbieter-Dashboard"
              >
                <User className="w-6 h-6" />
                <span>Profil</span>
              </Link>
            )}
            
            {/* Mobile Provider Logout */}
            {isProviderLoggedIn && (
              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  handleProviderLogout();
                }}
                className="flex items-center space-x-2 w-full px-3 py-2 rounded-md text-left text-base font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                aria-label="Vom Anbieter-Konto abmelden"
              >
                <LogOut className="w-6 h-6" />
                <span>Abmelden</span>
              </button>
            )}
            
            {/* Mobile Für Ärzte Section - only show if provider is NOT logged in */}
            {!isProviderLoggedIn && (
              <div className="pt-2 border-t border-gray-200">
                <div className="px-3 py-2 text-sm font-medium text-gray-500">Anbieter</div>
                {doctorMenuItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center space-x-2 w-full px-6 py-2 rounded-md text-left text-base font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.name}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;